<template>
  <div style="position: relative; width: 100%; height: 100%;">
    <canvas :id="canvasId"></canvas>
  </div>
</template>

<script>
import Chart from 'chart.js/auto';

export default {
  name: 'SuperAdminBarChart',
  props: {
    canvasId: { type: String, required: true },
    labels: { type: Array, default: () => [] },
    datasets: { type: Array, default: () => [] },
    yLabel: { type: String, default: '' },
    horizontal: { type: Boolean, default: false },
  },
  data() {
    return {
      chartObj: null,
    };
  },
  mounted() {
    setTimeout(() => {
      this.initChart();
    }, 150);
  },
  beforeDestroy() {
    if (this.chartObj) {
      this.chartObj.destroy();
      this.chartObj = null;
    }
  },
  watch: {
    labels: { handler() { this.updateChart(); }, deep: true },
    datasets: { handler() { this.updateChart(); }, deep: true },
    yLabel: { handler() { this.updateChart(); } },
    '$vuetify.theme.dark': {
      handler() {
        if (this.chartObj) {
          this.applyTheme();
          this.chartObj.update();
        }
      },
    },
  },
  methods: {
    getThemeColors() {
      const isDark = this.$vuetify.theme.dark;
      const themes = this.$vuetify.theme.themes || {};
      const theme = themes[isDark ? 'dark' : 'light'] || {};

      let primary = theme.primary || '#1976D2';
      if (typeof primary === 'object') primary = primary.base || '#1976D2';

      let success = theme.success || '#4CAF50';
      if (typeof success === 'object') success = success.base || '#4CAF50';

      let warning = theme.warning || '#FB8C00';
      if (typeof warning === 'object') warning = warning.base || '#FB8C00';

      return {
        primary,
        success,
        warning,
        textColor: isDark ? '#e0e0e0' : '#555555',
        gridColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)',
      };
    },
    applyTheme() {
      const { textColor, gridColor } = this.getThemeColors();
      const opts = this.chartObj.options;
      opts.plugins.legend.labels.color = textColor;
      if (this.horizontal) {
        opts.scales.y.ticks.color = textColor;
        opts.scales.x.ticks.color = textColor;
        opts.scales.x.grid.color = gridColor;
      } else {
        opts.scales.x.ticks.color = textColor;
        opts.scales.y.ticks.color = textColor;
        opts.scales.y.grid.color = gridColor;
      }
    },
    buildDatasets() {
      const { primary, success, warning } = this.getThemeColors();
      const palette = [primary, success, warning, '#9C27B0', '#FF5722', '#00BCD4', '#795548'];
      return (this.datasets || []).map((ds, i) => ({
        ...ds,
        backgroundColor: ds.backgroundColor || palette[i % palette.length],
        borderRadius: 6,
        borderSkipped: false,
      }));
    },
    initChart() {
      const { textColor, gridColor } = this.getThemeColors();
      const ctx = document.getElementById(this.canvasId);
      if (!ctx) return;

      const indexAxis = this.horizontal ? 'y' : 'x';

      this.chartObj = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: this.labels,
          datasets: this.buildDatasets(),
        },
        options: {
          indexAxis,
          responsive: true,
          maintainAspectRatio: false,
          animation: { duration: 600, easing: 'easeInOutQuart' },
          plugins: {
            legend: { position: 'top', labels: { color: textColor, boxWidth: 12, padding: 16 } },
            tooltip: { mode: 'index', intersect: false },
          },
          scales: {
            x: {
              ticks: { color: textColor },
              grid: this.horizontal ? { color: gridColor } : { display: false },
              title: (this.horizontal && this.yLabel) ? { display: true, text: this.yLabel, color: textColor } : { display: false },
            },
            y: {
              beginAtZero: true,
              ticks: { color: textColor, precision: 0 },
              grid: this.horizontal ? { display: false } : { color: gridColor },
              title: (!this.horizontal && this.yLabel) ? { display: true, text: this.yLabel, color: textColor } : { display: false },
            },
          },
        },
      });
    },
    updateChart() {
      if (!this.chartObj) return;
      this.applyTheme();
      this.chartObj.data.labels = this.labels;
      this.chartObj.data.datasets = this.buildDatasets();

      // Update titles
      const { textColor } = this.getThemeColors();
      const opts = this.chartObj.options;
      opts.scales.x.title = (this.horizontal && this.yLabel) ? { display: true, text: this.yLabel, color: textColor } : { display: false };
      opts.scales.y.title = (!this.horizontal && this.yLabel) ? { display: true, text: this.yLabel, color: textColor } : { display: false };

      this.chartObj.update();
    },
  },
};
</script>
